import type { CmmdThreadArchive, PostCmmdThreadArchiveParams } from "@cmmd-forge/contracts";

import { request, type TransportConfig } from "./transport";

export interface ThreadArchiveApi {
  postThreadArchive(
    runtimeId: string,
    projectId: string,
    threadId: string,
    params: PostCmmdThreadArchiveParams,
  ): Promise<CmmdThreadArchive>;
}

function archivePath(runtimeId: string, projectId: string, threadId: string): string {
  return `/api/cmmd/threads/${encodeURIComponent(runtimeId)}/${encodeURIComponent(projectId)}/${encodeURIComponent(threadId)}/archive`;
}

function postThreadArchive(
  cfg: TransportConfig,
  runtimeId: string,
  projectId: string,
  threadId: string,
  params: PostCmmdThreadArchiveParams,
): Promise<CmmdThreadArchive> {
  return request<CmmdThreadArchive>(
    { method: "POST", path: archivePath(runtimeId, projectId, threadId), body: params },
    cfg,
  );
}

export function bindThreadArchiveApi(cfg: TransportConfig): ThreadArchiveApi["postThreadArchive"] {
  return (runtimeId, projectId, threadId, params) =>
    postThreadArchive(cfg, runtimeId, projectId, threadId, params);
}

interface RevisionedArchiveCommand {
  readonly action: NonNullable<PostCmmdThreadArchiveParams["action"]>;
  readonly operationId?: string;
  readonly expectedRevision: number;
  readonly force: boolean;
}

interface RevisionRetryInput<TResult extends { readonly revision: number }> {
  readonly action: RevisionedArchiveCommand["action"];
  readonly operationId?: string;
  readonly expectedRevision: number;
  readonly force: boolean;
  readonly submit: (command: RevisionedArchiveCommand) => Promise<TResult>;
  readonly refresh: () => Promise<{ readonly revision: number }>;
}

function isRevisionConflict(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && error.status === 409;
}

/** Retry one stale owner command with the revision returned by a fresh CMMD read. */
export async function submitThreadArchiveWithRevisionRetry<
  TResult extends { readonly revision: number },
>(input: RevisionRetryInput<TResult>): Promise<TResult> {
  const command: RevisionedArchiveCommand = {
    action: input.action,
    ...(input.operationId === undefined ? {} : { operationId: input.operationId }),
    expectedRevision: input.expectedRevision,
    force: input.force,
  };
  try {
    return await input.submit(command);
  } catch (error) {
    if (!isRevisionConflict(error)) throw error;
    const current = await input.refresh();
    return input.submit({ ...command, expectedRevision: current.revision });
  }
}
